/**
 * MCP Backend Tool Notification System
 *
 * Intercepts tool_activity events directly from the WebSocket bridge (before
 * chat panel filtering) and dispatches classified notifications so that toolbar
 * icons and left-panel components can show visual feedback (shimmer, glow, tooltips).
 *
 * Architecture:
 *   WebSocket chat_event (tool_activity) → processRawToolActivity() → classify
 *     → MCP_BACKEND_NOTIFICATION_EVENT → toolbar-left / datasources / language panel
 */

// ── Event constant ──

export const MCP_BACKEND_NOTIFICATION_EVENT = "mcp-backend-notification";

// ── Types ──

export type BackendNotificationTarget =
  | "toolbar-datasources"
  | "toolbar-scheduled-jobs"
  | "toolbar-api-management"
  | "panel-datasources"
  | "panel-language";

export interface BackendNotificationPayload {
  target: BackendNotificationTarget;
  toolName: string;
  operation: string;
  message: string;
  isDelete: boolean;
  timestamp: number;
  itemName?: string;
  /** When true, only open the panel tab — don't highlight an item yet */
  openOnly?: boolean;
  /** Optional detail lines shown vertically below the message */
  detailLines?: string[];
}

// ── Classification ──

interface ToolNotificationRule {
  target: BackendNotificationTarget;
  messageFn: (input: Record<string, any>) => string;
  detailLinesFn?: (input: Record<string, any>) => string[] | undefined;
  isDelete?: boolean;
}

type OperationRuleMap = Record<string, ToolNotificationRule[]>;

/**
 * The Backend `database_schema` tool carries the affected table at
 * `table.name` (and, for column ops, the column at `column.name`).
 * `classifyBackendTool` passes the raw tool_activity event as `input`, so the
 * real tool args live under `parameters`. Read both shapes defensively.
 */
function dbSchemaTableName(input: Record<string, any>): string {
  return input?.table?.name || input?.parameters?.table?.name || input?.tableName || input?.parameters?.tableName || "";
}

const TOOL_RULES: Record<string, OperationRuleMap> = {
  db_sql_action: {
    save_action: [
      {
        target: "panel-datasources",
        messageFn: (input) => `SQL action "${input?.actionName || input?.parameters?.actionName || ""}" created`
      }
    ],
    save_datasource: [
      {
        target: "toolbar-datasources",
        messageFn: (input) =>
          `DB datasource "${input?.datasourceName || input?.parameters?.datasourceName || ""}" created`
      }
    ],
    save_multiple: [
      {
        target: "panel-datasources",
        messageFn: () => "Multiple SQL actions created"
      }
    ]
  },
  ds_action: {
    delete: [
      {
        target: "panel-datasources",
        messageFn: () => "Action deleted",
        isDelete: true
      }
    ]
  },
  ds_sap: {
    save_action: [
      {
        target: "panel-datasources",
        messageFn: (input) => `SAP action "${input?.functionName || input?.parameters?.functionName || ""}" created`
      },
      {
        target: "toolbar-datasources",
        messageFn: (input) => `SAP action "${input?.functionName || input?.parameters?.functionName || ""}" created`
      }
    ],
    save_datasource: [
      {
        target: "toolbar-datasources",
        messageFn: (input) =>
          `SAP datasource "${input?.datasourceName || input?.parameters?.datasourceName || ""}" created`
      }
    ]
  },
  // Real backend `database_schema` operations — mirror of the `operation`
  // switch in `Backend/CoreApi/Mcp/DbSchemaMcp.cs` (and
  // `mcp-chat-server/src/tools/confirmation-policy.ts` DATABASE_SCHEMA_OPERATIONS).
  // The Backend Architect emits `create_table` on every multi-screen build —
  // it's the single most frequent backend mutation — so this is what drives
  // the toolbar datasource shimmer. The prior rule keyed off a phantom
  // `execute_migration` op that the backend never dispatches, so the toolbar
  // reveal silently never fired for any table work. Read-only ops
  // (get_schema_name / get_tables / search_tables / get_table_schema /
  // get_column_types) intentionally have no rule.
  database_schema: {
    create_table: [
      {
        target: "toolbar-datasources",
        messageFn: (input) => `Table "${dbSchemaTableName(input)}" created`
      }
    ],
    add_column: [
      {
        target: "toolbar-datasources",
        messageFn: (input) => `Column added to "${dbSchemaTableName(input)}"`
      }
    ],
    alter_column: [
      {
        target: "toolbar-datasources",
        messageFn: (input) => `Column updated in "${dbSchemaTableName(input)}"`
      }
    ],
    rename_table: [
      {
        target: "toolbar-datasources",
        messageFn: (input) => `Table "${dbSchemaTableName(input)}" renamed`
      }
    ],
    rename_column: [
      {
        target: "toolbar-datasources",
        messageFn: (input) => `Column renamed in "${dbSchemaTableName(input)}"`
      }
    ],
    drop_table: [
      {
        target: "toolbar-datasources",
        messageFn: (input) => `Table "${dbSchemaTableName(input)}" dropped`,
        isDelete: true
      }
    ],
    drop_column: [
      {
        target: "toolbar-datasources",
        messageFn: (input) => `Column dropped from "${dbSchemaTableName(input)}"`,
        isDelete: true
      }
    ]
  },
  // Demo seed data (KOA-17889) — inserts rows into user tables, so reveal the
  // datasources surface where the populated tables are browsable.
  database_data: {
    seed: [
      {
        target: "toolbar-datasources",
        messageFn: () => "Demo data seeded"
      }
    ]
  },
  ds_csharp: {
    save: [
      {
        target: "toolbar-datasources",
        messageFn: (input) =>
          `C# datasource "${input?.datasourceName || input?.parameters?.datasourceName || ""}" saved`
      }
    ]
  },
  ds_code: {
    save: [
      {
        target: "toolbar-datasources",
        messageFn: (input) =>
          `${input?.language || input?.parameters?.language || "Code"} datasource "${input?.datasourceName || input?.parameters?.datasourceName || ""}" saved`
      }
    ]
  },
  ds_datasource: {
    save_swagger: [
      {
        target: "toolbar-datasources",
        messageFn: (input) =>
          `REST datasource "${input?.actionUniqueName || input?.parameters?.actionUniqueName || ""}" created`
      }
    ],
    delete: [
      {
        target: "toolbar-datasources",
        messageFn: () => "Datasource deleted",
        isDelete: true
      }
    ]
  },
  package_management: {
    install: [
      {
        target: "toolbar-datasources",
        messageFn: (input) => `Package "${input?.packageName || input?.parameters?.packageName || ""}" installed`
      }
    ],
    update: [
      {
        target: "toolbar-datasources",
        messageFn: (input) => `Package "${input?.packageName || input?.parameters?.packageName || ""}" updated`
      }
    ],
    uninstall: [
      {
        target: "toolbar-datasources",
        messageFn: () => "Package removed",
        isDelete: true
      }
    ]
  },
  offline_table: {
    save: [
      {
        target: "toolbar-datasources",
        messageFn: (input) => `Offline table "${input?.tableName || input?.parameters?.tableName || ""}" saved`
      }
    ],
    remove: [
      {
        target: "toolbar-datasources",
        messageFn: (input) => `Offline table "${input?.tableName || input?.parameters?.tableName || ""}" removed`,
        isDelete: true
      }
    ]
  },
  scheduler_job: {
    create: [
      {
        target: "toolbar-scheduled-jobs",
        messageFn: (input) => `Job "${input?.name || input?.parameters?.name || ""}" created`
      }
    ],
    update: [
      {
        target: "toolbar-scheduled-jobs",
        messageFn: (input) => `Job "${input?.name || input?.parameters?.name || ""}" updated`
      }
    ],
    delete: [
      {
        target: "toolbar-scheduled-jobs",
        messageFn: () => "Job deleted",
        isDelete: true
      }
    ],
    activate: [
      {
        target: "toolbar-scheduled-jobs",
        messageFn: () => "Job activated"
      }
    ],
    inactivate: [
      {
        target: "toolbar-scheduled-jobs",
        messageFn: () => "Job deactivated"
      }
    ]
  },
  api_management: {
    save_api: [
      {
        target: "toolbar-api-management",
        messageFn: () => "API saved"
      }
    ],
    create_controller: [
      {
        target: "toolbar-api-management",
        messageFn: (input) => `Controller "${input?.controllerName || input?.parameters?.controllerName || ""}" created`
      }
    ],
    delete_controller: [
      {
        target: "toolbar-api-management",
        messageFn: () => "Controller deleted",
        isDelete: true
      }
    ],
    remove_api: [
      {
        target: "toolbar-api-management",
        messageFn: () => "API removed",
        isDelete: true
      }
    ]
  },
  translation: {
    add_language: [
      {
        target: "panel-language",
        messageFn: () => "Language added"
      }
    ],
    remove_language: [
      {
        target: "panel-language",
        messageFn: () => "Language removed",
        isDelete: true
      }
    ],
    save: [
      {
        target: "panel-language",
        messageFn: () => "Translations saved"
      }
    ],
    delete_all: [
      {
        target: "panel-language",
        messageFn: () => "All translations deleted",
        isDelete: true
      }
    ],
    delete_screen: [
      {
        target: "panel-language",
        messageFn: () => "Screen translations deleted",
        isDelete: true
      }
    ],
    set_default: [
      {
        target: "panel-language",
        messageFn: () => "Default language changed"
      }
    ]
  }
};

/**
 * Classify a backend tool activity event into notification payloads.
 * Returns an empty array for read-only operations or unknown tools.
 */
export function classifyBackendTool(toolName: string, input: Record<string, any>): BackendNotificationPayload[] {
  const operation: string = input?.parameters?.operation || input?.operation || "";

  const toolRules = TOOL_RULES[toolName];
  if (!toolRules) return [];

  const opRules = toolRules[operation];
  if (!opRules) return [];

  const now = Date.now();
  return opRules.map((rule) => ({
    target: rule.target,
    toolName,
    operation,
    message: rule.messageFn(input),
    isDelete: rule.isDelete || false,
    timestamp: now,
    itemName:
      input?.actionName ||
      input?.parameters?.actionName ||
      input?.datasourceName ||
      input?.parameters?.datasourceName ||
      input?.name ||
      input?.parameters?.name ||
      input?.tableName ||
      input?.parameters?.tableName ||
      input?.table?.name ||
      input?.parameters?.table?.name ||
      input?.functionName ||
      input?.parameters?.functionName ||
      undefined,
    detailLines: rule.detailLinesFn?.(input)
  }));
}

// ── Dispatch helper ──

function dispatchNotification(payload: BackendNotificationPayload): void {
  window.dispatchEvent(new CustomEvent(MCP_BACKEND_NOTIFICATION_EVENT, { detail: payload }));
}

// ── Pending panel notifications ──
// Panel components (datasources, language) may not be mounted when a notification
// fires. The toolbar-left switches the view, and the panel checks pending
// notifications on mount.

const pendingPanelNotifications: Map<BackendNotificationTarget, BackendNotificationPayload[]> = new Map();
const PENDING_TTL_MS = 5000;

function addPendingNotification(payload: BackendNotificationPayload): void {
  const existing = pendingPanelNotifications.get(payload.target) || [];
  existing.push(payload);
  pendingPanelNotifications.set(payload.target, existing);

  // Auto-expire after TTL
  setTimeout(() => {
    const current = pendingPanelNotifications.get(payload.target);
    if (current) {
      const filtered = current.filter((n) => n.timestamp !== payload.timestamp);
      if (filtered.length === 0) {
        pendingPanelNotifications.delete(payload.target);
      } else {
        pendingPanelNotifications.set(payload.target, filtered);
      }
    }
  }, PENDING_TTL_MS);
}

/**
 * Consume pending notifications for a panel target.
 * Called by panel components on mount to pick up notifications that
 * arrived before the component was rendered.
 */
export function consumePendingNotifications(target: BackendNotificationTarget): BackendNotificationPayload[] {
  const pending = pendingPanelNotifications.get(target) || [];
  pendingPanelNotifications.delete(target);
  return pending;
}

// ── Primary entry point ──
// Called directly from mcp-tool-bridge.ts when a chat_event with
// event="tool_activity" arrives via WebSocket — before any chat panel filtering.

// ── Deferred panel notifications ──
// Backend tools create resources, but the UI data (sd.actionDefinitions) only
// updates after ui_project.reload_datasources runs. We store panel notifications
// and re-dispatch them when the reload completes.

const deferredPanelNotifications: BackendNotificationPayload[] = [];

/**
 * Process a raw tool_activity event from the WebSocket bridge.
 * Classifies the tool and dispatches notifications if applicable.
 *
 * @param data - The raw SSE/WebSocket data: `{ toolName, parameters, status, result, duration }`
 */
export function processRawToolActivity(data: Record<string, any>): void {
  if (!data?.toolName) return;
  if (data.status === "running") return;
  if (data.status === "error") return;

  // When ui_project.reload_datasources completes, flush deferred panel notifications
  if (data.toolName === "ui_project" && data.parameters?.operation === "reload_datasources") {
    if (deferredPanelNotifications.length > 0) {
      const toFlush = deferredPanelNotifications.splice(0);
      for (const notif of toFlush) {
        addPendingNotification(notif);
        dispatchNotification(notif);
      }
    }
    return;
  }

  const notifications = classifyBackendTool(data.toolName, data);
  if (notifications.length === 0) return;

  for (const notif of notifications) {
    if (notif.target.startsWith("panel-")) {
      // Dispatch immediate "open tab" notification (no highlight yet)
      dispatchNotification({ ...notif, openOnly: true, itemName: undefined });
      // Defer the highlight notification until reload_datasources completes
      deferredPanelNotifications.push(notif);
    }

    // Toolbar notifications dispatch immediately (no data dependency)
    if (notif.target.startsWith("toolbar-")) {
      dispatchNotification(notif);
    }
  }
}
